const SITE_URL = "https://bandflow.es";
const LOGO_URL = `${SITE_URL}/bandflow-logo.png`;

export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": `${SITE_URL}/#organization`,
    name: "Bandflow",
    url: SITE_URL,
    logo: {
      "@type": "ImageObject",
      url: LOGO_URL,
    },
  };
}

export function webSiteSchema() {
  return {
    "@type": "WebSite",
    "@id": `${SITE_URL}/#website`,
    name: "Bandflow",
    url: SITE_URL,
    publisher: { "@id": `${SITE_URL}/#organization` },
  };
}

export function breadcrumbSchema(items: { name: string; path: string }[]) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: items.map((item, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: item.name,
      item: `${SITE_URL}${item.path}`,
    })),
  };
}

interface VenueListItem {
  name: string;
  slug: string | null;
  city: string;
  capacity?: number | null;
  main_image?: string | null;
  avg_rating?: number | null;
  review_count?: number | null;
}

export function collectionPageSchema(
  name: string,
  canonicalPath: string,
  venues: VenueListItem[],
  aboutEntity?: Record<string, any>
) {
  const schema: Record<string, any> = {
    "@type": "CollectionPage",
    name,
    url: `${SITE_URL}${canonicalPath}`,
    mainEntity: {
      "@type": "ItemList",
      itemListElement: venues.map((v, i) => {
        const item: Record<string, any> = {
          "@type": "MusicVenue",
          name: v.name,
          url: `${SITE_URL}/sala/${v.slug}/`,
        };
        if (v.main_image) item.image = v.main_image;
        if (v.city) item.address = { "@type": "PostalAddress", addressLocality: v.city };
        if (v.capacity) item.maximumAttendeeCapacity = v.capacity;
        if (v.avg_rating != null && v.review_count && v.review_count > 0) {
          item.aggregateRating = {
            "@type": "AggregateRating",
            ratingValue: Math.round(v.avg_rating * 10) / 10,
            reviewCount: v.review_count,
          };
        }
        return {
          "@type": "ListItem",
          position: i + 1,
          item,
        };
      }),
    },
  };
  if (aboutEntity) schema.about = aboutEntity;
  return schema;
}

interface MusicVenueData {
  name: string;
  slug: string;
  city: string;
  autonomous_community?: string | null;
  address_street?: string | null;
  address_number?: string | null;
  postal_code?: string | null;
  capacity?: number | null;
  email?: string | null;
  phone?: string | null;
  website?: string | null;
  instagram?: string | null;
  main_image?: string | null;
  // Official services
  backline_available?: boolean | null;
  rider_available?: boolean | null;
  sound_tech_official?: boolean | null;
  audio_recording?: boolean | null;
  poster_design?: boolean | null;
  merch_area_official?: boolean | null;
  catering_official?: boolean | null;
  ticketing_type?: string | null;
}

export function musicVenueSchema(
  venue: MusicVenueData,
  avgRating: number | null,
  reviewCount: number,
  mainImageUrl?: string | null
) {
  const schema: Record<string, any> = {
    "@type": "MusicVenue",
    name: venue.name,
    url: `${SITE_URL}/sala/${venue.slug}/`,
  };

  // Image
  const image = mainImageUrl ?? venue.main_image;
  if (image) schema.image = image;

  // Address
  const streetAddress = [venue.address_street, venue.address_number].filter(Boolean).join(" ");
  const address: Record<string, any> = {
    "@type": "PostalAddress",
    addressLocality: venue.city,
    addressCountry: "ES",
  };
  if (streetAddress) address.streetAddress = streetAddress;
  if (venue.postal_code) address.postalCode = venue.postal_code;
  if (venue.autonomous_community) address.addressRegion = venue.autonomous_community;
  schema.address = address;

  // Contact info (only if shown)
  if (venue.phone) schema.telephone = venue.phone;
  if (venue.email) schema.email = venue.email;

  // sameAs
  const sameAs: string[] = [];
  if (venue.website) sameAs.push(venue.website);
  if (venue.instagram) {
    const handle = venue.instagram.replace(/^@/, "").replace(/\/+$/, "");
    if (handle.includes("instagram.com")) sameAs.push(venue.instagram);
    else sameAs.push(`https://instagram.com/${handle}`);
  }
  if (sameAs.length) schema.sameAs = sameAs;

  // Capacity
  if (venue.capacity) schema.maximumAttendeeCapacity = venue.capacity;

  // AggregateRating
  if (avgRating != null && reviewCount > 0) {
    schema.aggregateRating = {
      "@type": "AggregateRating",
      ratingValue: Math.round(avgRating * 10) / 10,
      reviewCount,
    };
  }

  // amenityFeature from official services
  const features: Record<string, any>[] = [];

  const addFeature = (name: string, value: boolean | null | undefined) => {
    if (value === null || value === undefined) return;
    features.push({
      "@type": "LocationFeatureSpecification",
      name,
      value,
    });
  };

  // Backline / Rider: use either
  const backlineRider = venue.backline_available ?? venue.rider_available;
  addFeature("Backline / Rider", backlineRider);
  addFeature("Técnico de sonido", venue.sound_tech_official);
  addFeature("Grabación audio", venue.audio_recording);
  addFeature("Cartelería", venue.poster_design);
  addFeature("Merchandising", venue.merch_area_official);
  addFeature("Catering", venue.catering_official);

  // Ticketing (string value)
  if (venue.ticketing_type) {
    const ticketingValue =
      venue.ticketing_type === "obligatorio"
        ? "Propio (obligatorio)"
        : venue.ticketing_type === "libre"
        ? "Libre"
        : venue.ticketing_type;
    features.push({
      "@type": "LocationFeatureSpecification",
      name: "Ticketing",
      value: ticketingValue,
    });
  }

  if (features.length) schema.amenityFeature = features;

  return schema;
}
